import React from 'react';

/**
 * Strategy Intelligence System — hero visualization
 *
 * Drop-in location: /src/visualizations/StrategyIntelligenceSystem.jsx
 *
 * Fonts: requires Montserrat. Add to your global CSS (src/index.css or main.css):
 *   @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
 *
 * Usage:
 *   import StrategyIntelligenceSystem from './visualizations/StrategyIntelligenceSystem';
 *   <Route path="/viz/sip-system" element={<StrategyIntelligenceSystem />} />
 */

const NAVY = '#18365E';
const CORAL = '#FF6E4C';
const BG = '#F5F7FA';
const MUTED = '#6B7B8C';
const FAINT = '#8A96A3';
const HAIRLINE = '#E4E9EF';

const LayerLabel = ({ tag, title, subtitle, accent, border }) => (
  <div
    style={{
      padding: '32px 28px',
      borderLeft: border ? `1px solid ${HAIRLINE}` : undefined,
    }}
  >
    <div
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.26em',
        color: accent,
        marginBottom: 10,
      }}
    >
      {tag}
    </div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 600,
        color: NAVY,
        marginBottom: 10,
        letterSpacing: '-0.015em',
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontSize: 13,
        color: MUTED,
        lineHeight: 1.6,
        fontWeight: 400,
      }}
    >
      {subtitle}
    </div>
  </div>
);

const StrategyIntelligenceSystem = () => {
  return (
    <div
      style={{
        background: BG,
        minHeight: '100vh',
        padding: '80px 32px',
        fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: NAVY,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes sip-pulse {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.06); }
        }
        @keyframes sip-flow {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -28; }
        }
        @keyframes sip-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sip-halo { transform-origin: 500px 350px; animation: sip-pulse 4s ease-in-out infinite; }
        .sip-feedback-arc { stroke-dasharray: 5 4; animation: sip-flow 2.2s linear infinite; }
        .sip-stagger > * { animation: sip-fade-in 0.8s ease-out backwards; }
        .sip-stagger > *:nth-child(1) { animation-delay: 0.0s; }
        .sip-stagger > *:nth-child(2) { animation-delay: 0.1s; }
        .sip-stagger > *:nth-child(3) { animation-delay: 0.2s; }
        .sip-stagger > *:nth-child(4) { animation-delay: 0.3s; }
      `}</style>

      {/* Background texture — subtle dot grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle, ${NAVY}14 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
          opacity: 0.6,
          pointerEvents: 'none',
        }}
      />
      {/* Background glow from center */}
      <div
        style={{
          position: 'absolute',
          top: '45%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 900,
          height: 900,
          background: `radial-gradient(circle, ${CORAL}14 0%, ${NAVY}08 30%, transparent 60%)`,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <header className="sip-stagger" style={{ textAlign: 'center', marginBottom: 56 }}>
          <div
            style={{
              display: 'inline-block',
              color: CORAL,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              borderTop: `1px solid ${CORAL}`,
              borderBottom: `1px solid ${CORAL}`,
              padding: '8px 18px',
              marginBottom: 28,
            }}
          >
            Strategy Intelligence System
          </div>
          <h1
            style={{
              fontSize: 'clamp(36px, 5.5vw, 62px)',
              fontWeight: 300,
              margin: 0,
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              color: NAVY,
            }}
          >
            Decision infrastructure,
            <br />
            <span style={{ fontWeight: 600 }}>built to compound.</span>
          </h1>
          <p
            style={{
              fontSize: 15,
              color: MUTED,
              maxWidth: 640,
              margin: '28px auto 0',
              lineHeight: 1.65,
              fontWeight: 400,
            }}
          >
            One engine at the core. One platform around it. A growing set of applications on top.
            Every engagement adds proof back into the system.
          </p>
        </header>

        {/* Hero SVG */}
        <svg
          viewBox="0 0 1000 700"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: 'auto', display: 'block', maxWidth: 1100, margin: '0 auto' }}
          role="img"
          aria-labelledby="sip-title sip-desc"
        >
          <title id="sip-title">Strategy Intelligence System architecture</title>
          <desc id="sip-desc">
            A radial system map. SIE, the Strategy Intelligence Engine, sits at the center with a
            coral halo. It is wrapped by the SIP platform ring. Four SIA applications are
            positioned at compass points: DVA live in coral at top, MSIA and CSIA planned on
            right and bottom, and future SIAs as a ghost node on the left. GTM activities —
            fundraising, working sessions, and paid pilots — occupy the outer corners with
            inflow arrows. A coral feedback arc shows proof compounding back into the engine.
          </desc>

          <defs>
            <radialGradient id="sieRadial" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={CORAL} stopOpacity="0.18" />
              <stop offset="50%" stopColor={NAVY} stopOpacity="0.05" />
              <stop offset="100%" stopColor={NAVY} stopOpacity="0" />
            </radialGradient>
            <marker
              id="arrowCoral"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path d="M0 0 L10 5 L0 10 Z" fill={CORAL} />
            </marker>
            <marker
              id="arrowNavy"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M0 0 L10 5 L0 10" fill="none" stroke={NAVY} strokeOpacity="0.5" strokeWidth="1.3" />
            </marker>
          </defs>

          {/* Ambient glow behind SIE */}
          <circle cx="500" cy="350" r="340" fill="url(#sieRadial)" />

          {/* SIP ring */}
          <circle
            cx="500"
            cy="350"
            r="220"
            fill="none"
            stroke={NAVY}
            strokeOpacity="0.22"
            strokeWidth="1"
            strokeDasharray="2 6"
          />
          <text
            x="500"
            y="118"
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            letterSpacing="3.5"
            fill={FAINT}
          >
            STRATEGY INTELLIGENCE PLATFORM
          </text>

          {/* Radial spokes from SIE to SIAs (faint) */}
          <g stroke={NAVY} strokeOpacity="0.14" strokeWidth="0.6">
            <line x1="500" y1="350" x2="500" y2="134" />
            <line x1="500" y1="350" x2="716" y2="350" />
            <line x1="500" y1="350" x2="500" y2="566" />
            <line x1="500" y1="350" x2="284" y2="350" />
          </g>

          {/* =========== GTM — NE quadrant: Fundraising =========== */}
          <g>
            <circle cx="770" cy="140" r="3.5" fill={NAVY} />
            <line
              x1="767"
              y1="150"
              x2="720"
              y2="335"
              stroke={NAVY}
              strokeOpacity="0.32"
              strokeWidth="0.6"
              strokeDasharray="3 4"
              markerEnd="url(#arrowNavy)"
            />
            <text x="770" y="124" textAnchor="middle" fontSize="13" fontWeight="600" fill={NAVY}>
              Fundraising
            </text>
            <text
              x="770"
              y="96"
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              letterSpacing="2.2"
              fill={FAINT}
            >
              ANGELS · F&amp;F · OPERATORS
            </text>
          </g>

          {/* =========== GTM — SE quadrant: Paid pilots =========== */}
          <g>
            <circle cx="770" cy="560" r="3.5" fill={NAVY} />
            <line
              x1="767"
              y1="550"
              x2="720"
              y2="365"
              stroke={NAVY}
              strokeOpacity="0.32"
              strokeWidth="0.6"
              strokeDasharray="3 4"
              markerEnd="url(#arrowNavy)"
            />
            <text x="770" y="590" textAnchor="middle" fontSize="13" fontWeight="600" fill={NAVY}>
              Paid pilots
            </text>
            <text
              x="770"
              y="612"
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              letterSpacing="2.2"
              fill={FAINT}
            >
              ASSESSMENT · SPRINT
            </text>
          </g>

          {/* =========== GTM — SW quadrant: Working sessions =========== */}
          <g>
            <circle cx="230" cy="560" r="3.5" fill={NAVY} />
            <line
              x1="233"
              y1="550"
              x2="280"
              y2="365"
              stroke={NAVY}
              strokeOpacity="0.32"
              strokeWidth="0.6"
              strokeDasharray="3 4"
              markerEnd="url(#arrowNavy)"
            />
            <text x="230" y="590" textAnchor="middle" fontSize="13" fontWeight="600" fill={NAVY}>
              Working sessions
            </text>
            <text
              x="230"
              y="612"
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              letterSpacing="2.2"
              fill={FAINT}
            >
              ENTRY · QUALIFY
            </text>
          </g>

          {/* =========== Feedback arc — NW quadrant =========== */}
          <g>
            <path
              className="sip-feedback-arc"
              d="M 220 180 Q 340 200 420 290"
              fill="none"
              stroke={CORAL}
              strokeWidth="1.8"
              markerEnd="url(#arrowCoral)"
            />
            <text x="230" y="124" textAnchor="middle" fontSize="13" fontWeight="600" fill={CORAL}>
              Proof compounds
            </text>
            <text
              x="230"
              y="96"
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              letterSpacing="2.2"
              fill={CORAL}
              opacity="0.85"
            >
              ROI · VALIDATION · REVENUE
            </text>
          </g>

          {/* =========== SIAs =========== */}
          {/* DVA — North (LIVE) */}
          <g>
            <text x="500" y="68" textAnchor="middle" fontSize="18" fontWeight="600" fill={NAVY}>
              DVA
            </text>
            <text x="500" y="86" textAnchor="middle" fontSize="11" fontWeight="400" fill={MUTED}>
              Data Value Accelerator
            </text>
            <circle cx="500" cy="130" r="28" fill="none" stroke={CORAL} strokeOpacity="0.25" strokeWidth="1" />
            <circle cx="500" cy="130" r="20" fill={CORAL} />
            <text
              x="500"
              y="135"
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill="#FFFFFF"
              letterSpacing="1"
            >
              DVA
            </text>
            <rect x="478" y="164" width="44" height="16" rx="8" fill={CORAL} />
            <text
              x="500"
              y="175"
              textAnchor="middle"
              fontSize="8"
              fontWeight="700"
              letterSpacing="1.5"
              fill="#FFFFFF"
            >
              LIVE
            </text>
          </g>

          {/* MSIA — East */}
          <g>
            <text x="720" y="304" textAnchor="middle" fontSize="17" fontWeight="600" fill={NAVY}>
              MSIA
            </text>
            <text x="720" y="322" textAnchor="middle" fontSize="11" fontWeight="400" fill={MUTED}>
              Market Strategy
            </text>
            <circle cx="720" cy="350" r="18" fill="#FFFFFF" stroke={NAVY} strokeWidth="1.5" />
            <text
              x="720"
              y="355"
              textAnchor="middle"
              fontSize="8"
              fontWeight="700"
              fill={NAVY}
              letterSpacing="1"
            >
              MSIA
            </text>
            <rect
              x="694"
              y="380"
              width="52"
              height="14"
              rx="7"
              fill="none"
              stroke={FAINT}
              strokeWidth="0.6"
            />
            <text
              x="720"
              y="390"
              textAnchor="middle"
              fontSize="7"
              fontWeight="600"
              letterSpacing="1.4"
              fill={FAINT}
            >
              PLANNED
            </text>
          </g>

          {/* CSIA — South */}
          <g>
            <circle cx="500" cy="570" r="18" fill="#FFFFFF" stroke={NAVY} strokeWidth="1.5" />
            <text
              x="500"
              y="575"
              textAnchor="middle"
              fontSize="8"
              fontWeight="700"
              fill={NAVY}
              letterSpacing="1"
            >
              CSIA
            </text>
            <rect
              x="474"
              y="600"
              width="52"
              height="14"
              rx="7"
              fill="none"
              stroke={FAINT}
              strokeWidth="0.6"
            />
            <text
              x="500"
              y="610"
              textAnchor="middle"
              fontSize="7"
              fontWeight="600"
              letterSpacing="1.4"
              fill={FAINT}
            >
              PLANNED
            </text>
            <text x="500" y="636" textAnchor="middle" fontSize="17" fontWeight="600" fill={NAVY}>
              CSIA
            </text>
            <text x="500" y="654" textAnchor="middle" fontSize="11" fontWeight="400" fill={MUTED}>
              Contracting Strategy
            </text>
          </g>

          {/* Future SIAs — West */}
          <g>
            <text x="280" y="304" textAnchor="middle" fontSize="14" fontWeight="600" fill={FAINT}>
              Future SIAs
            </text>
            <text x="280" y="322" textAnchor="middle" fontSize="11" fontWeight="400" fill="#B8C3D0">
              Expansion domains
            </text>
            <circle
              cx="280"
              cy="350"
              r="18"
              fill="#FFFFFF"
              stroke="#B8C3D0"
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
            <text x="280" y="356" textAnchor="middle" fontSize="16" fontWeight="400" fill="#B8C3D0">
              +
            </text>
          </g>

          {/* =========== SIE core (on top of everything) =========== */}
          <circle
            className="sip-halo"
            cx="500"
            cy="350"
            r="116"
            fill="none"
            stroke={CORAL}
            strokeWidth="1.2"
            strokeOpacity="0.3"
          />
          <circle cx="500" cy="350" r="95" fill={NAVY} />
          <circle
            cx="500"
            cy="350"
            r="95"
            fill="none"
            stroke={CORAL}
            strokeWidth="1.8"
          />

          <text
            x="500"
            y="346"
            textAnchor="middle"
            fontSize="56"
            fontWeight="500"
            fill="#FFFFFF"
            letterSpacing="2"
          >
            SIE
          </text>
          <line x1="476" y1="364" x2="524" y2="364" stroke={CORAL} strokeWidth="0.8" />
          <text
            x="500"
            y="382"
            textAnchor="middle"
            fontSize="8"
            fontWeight="600"
            letterSpacing="2.8"
            fill={CORAL}
          >
            STRATEGY INTELLIGENCE
          </text>
          <text
            x="500"
            y="396"
            textAnchor="middle"
            fontSize="8"
            fontWeight="600"
            letterSpacing="2.8"
            fill={CORAL}
          >
            ENGINE
          </text>
        </svg>

        {/* Layer legend */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0,
            marginTop: 64,
            borderTop: `1px solid ${HAIRLINE}`,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <LayerLabel
            tag="01 · CORE"
            title="SIE"
            subtitle="The engine we own. AI, rules, ROI logic, and governance — compounding with every pilot."
            accent={CORAL}
          />
          <LayerLabel
            tag="02 · PLATFORM"
            title="SIP"
            subtitle="The persistent container. Extends across decision domains without rearchitecture."
            accent={NAVY}
            border
          />
          <LayerLabel
            tag="03 · APPLICATIONS"
            title="SIAs"
            subtitle="DVA today. MSIA and CSIA next. Each a separately packageable decision product."
            accent={NAVY}
            border
          />
        </div>

        {/* Footer */}
        <footer style={{ textAlign: 'center', marginTop: 48 }}>
          <p
            style={{
              fontSize: 18,
              fontStyle: 'italic',
              color: NAVY,
              margin: '0 0 20px',
              fontWeight: 300,
              letterSpacing: '-0.005em',
            }}
          >
            "Every pilot compounds the engine."
          </p>
          <div
            style={{
              fontSize: 9,
              letterSpacing: '0.24em',
              color: FAINT,
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Adaptive Product &middot; Strategy Intelligence Platform &middot; Confidential
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StrategyIntelligenceSystem;
